/**
 * The sidebar's attention badges (spec 2026-10-27-23): what each entry
 * shows, computed from `GET /users/me/attention`. Pure, so the rules are unit
 * tested (attention.test.ts).
 */
import type { components } from '../api/schema'

export type AttentionCounts = components['schemas']['AttentionCounts']

/** The four menu entries that carry a badge. */
export const ATTENTION_ENTRIES = ['steering', 'conversations', 'documents', 'contracts'] as const
export type AttentionEntry = (typeof ATTENTION_ENTRIES)[number]

export function isAttentionEntry(id: string): id is AttentionEntry {
    return (ATTENTION_ENTRIES as readonly string[]).includes(id)
}

/** What one entry shows: a red count, a neutral count, or nothing. */
export type EntryBadge =
    | { kind: 'red'; count: number }
    | { kind: 'neutral'; count: number }
    | { kind: 'none' }

export function entryBadge(entry: AttentionEntry, counts: AttentionCounts | undefined): EntryBadge {
    if (!counts) return { kind: 'none' }
    switch (entry) {
        case 'steering': {
            const n = counts.issues?.total ?? 0
            return n > 0 ? { kind: 'red', count: n } : { kind: 'none' }
        }
        case 'conversations': {
            // Unread wins; with nothing unread, the entry keeps the neutral
            // count of unresolved senders it always had.
            const unread = counts.conversations?.unread ?? 0
            if (unread > 0) return { kind: 'red', count: unread }
            const unresolved = counts.conversations?.unresolved ?? 0
            return unresolved > 0 ? { kind: 'neutral', count: unresolved } : { kind: 'none' }
        }
        case 'documents': {
            const n = counts.documents?.to_validate ?? 0
            return n > 0 ? { kind: 'red', count: n } : { kind: 'none' }
        }
        case 'contracts': {
            const n = counts.contracts?.notice_due ?? 0
            return n > 0 ? { kind: 'red', count: n } : { kind: 'none' }
        }
    }
}

/** One line of a badge's tooltip: an i18n key under `nav.attention` and its count. */
export interface TooltipPart {
    key: string
    count: number
}

export function tooltipParts(entry: AttentionEntry, counts: AttentionCounts | undefined): TooltipPart[] {
    if (!counts) return []
    const parts: TooltipPart[] = []
    const push = (key: string, count: number | undefined) => {
        if (count && count > 0) parts.push({ key, count })
    }
    switch (entry) {
        case 'steering':
            push('new', counts.issues?.new)
            push('overdue', counts.issues?.overdue)
            push('follow_up_due', counts.issues?.follow_up_due)
            push('critical', counts.issues?.critical)
            break
        case 'conversations':
            push('unread', counts.conversations?.unread)
            push('unresolved', counts.conversations?.unresolved)
            break
        case 'documents':
            push('to_validate', counts.documents?.to_validate)
            break
        case 'contracts':
            push('notice_due', counts.contracts?.notice_due)
            break
    }
    return parts
}

/** The tab title, prefixed « (N) » while something waits. */
export function titleWithAttention(title: string, total: number): string {
    const bare = title.replace(/^\(\d+\+?\)\s+/, '')
    if (total <= 0) return bare
    return `(${total > 99 ? '99+' : total}) ${bare}`
}
